/* KönigPanda cart module: add / change with real error handling, quick add on
   product cards, and a cart drawer that re-renders via the Section Rendering API.
   Emits legacy `cart-change` so Electro's header counters stay in sync. */
(() => {
  // Electro common.js later replaces window.fetch with a wrapper that throws
  // parsed JSON on errors; keep the native implementation for predictable errors.
  const nativeFetch = window.fetch.bind(window);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const DRAWER_SECTION = 'kp-cart-drawer';

  const request = async (url, options = {}) => {
    const res = await nativeFetch(url, {
      ...options,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(options.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || 'Cart request failed');
      err.status = res.status;
      throw err;
    }
    return data;
  };

  const KpCart = {
    async add(formData) {
      formData.append('sections', DRAWER_SECTION);
      formData.append('sections_url', window.location.pathname);
      const added = await request(`${root}cart/add.js`, { method: 'POST', body: formData });
      return this.refresh(added.sections);
    },

    async change(line, quantity) {
      const body = JSON.stringify({ line, quantity, sections: [DRAWER_SECTION], sections_url: window.location.pathname });
      const data = await request(`${root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
      return this.refresh(data.sections, data);
    },

    async refresh(sections, cartData) {
      const cart = cartData && cartData.items ? cartData : await request(`${root}cart.js`);
      if (window.Cart) Object.assign(window.Cart, cart);
      document.dispatchEvent(new CustomEvent('cart-change', { detail: cart }));
      document.dispatchEvent(new CustomEvent('kp:cart-updated', { detail: { cart, sections } }));
      return cart;
    },
  };

  window.KpCart = KpCart;

  const flash = (button, stateClass, labelText, ms = 1800) => {
    const label = button.querySelector('span');
    const original = label ? label.textContent : '';
    button.classList.add(stateClass);
    if (label && labelText) label.textContent = labelText;
    setTimeout(() => {
      button.classList.remove(stateClass);
      if (label) label.textContent = original;
    }, ms);
  };

  /* Quick add (product cards) and any form marked data-kp-quick-add */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-kp-quick-add]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (!button || button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    const status = form.closest('.kp-card')?.querySelector('[data-kp-cart-status]');
    if (status) status.textContent = '';
    try {
      await KpCart.add(new FormData(form));
      button.classList.remove('is-loading');
      flash(button, 'is-added', window.kpStrings?.added);
      if (status) status.textContent = window.kpStrings?.added || '';
      document.dispatchEvent(new CustomEvent('kp:cart-open', { detail: { trigger: button } }));
    } catch (error) {
      button.classList.remove('is-loading');
      flash(button, 'is-error', window.kpStrings?.error, 2600);
      if (status) status.textContent = error.message || window.kpStrings?.error || '';
      document.dispatchEvent(new CustomEvent('kp:cart-error', { detail: { message: error.message } }));
    } finally {
      button.removeAttribute('aria-busy');
    }
  });
  /* ---------------------------------------------------------------------
     <kp-cart-drawer> — right-side cart. Needs window.KpDrawer (kp-nav.js). */
  const defineDrawer = () => {
    if (customElements.get('kp-cart-drawer') || !window.KpDrawer) return;

    class KpCartDrawer extends window.KpDrawer {
      connectedCallback() {
        super.connectedCallback();
        if (this.cartReady) return;
        this.cartReady = true;
        this.addEventListener('click', (event) => {
          const btn = event.target.closest('[data-kp-qty]');
          if (!btn) return;
          const line = parseInt(btn.closest('[data-line]').dataset.line, 10);
          this.changeLine(line, parseInt(btn.dataset.kpQty, 10), btn);
        });
      }

      async changeLine(line, quantity, btn) {
        if (this.busy) return;
        this.busy = true;
        this.setAttribute('aria-busy', 'true');
        this.focusKey = btn ? `${line}:${btn.dataset.kpQty > 0 ? (btn.getAttribute('aria-label') || '') : 'remove'}` : null;
        try {
          await KpCart.change(line, quantity);
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.busy = false;
          this.removeAttribute('aria-busy');
        }
      }

      showError(message) {
        const el = this.querySelector('[data-kp-cart-error]');
        if (!el) return;
        el.textContent = message || window.kpStrings?.error || '';
        el.hidden = false;
      }

      update(html) {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshPanel = doc.querySelector('#kp-cart-drawer [role="dialog"]');
        const panel = this.querySelector('[role="dialog"]');
        if (!freshPanel || !panel) return;
        const scroll = this.querySelector('[data-kp-cart-body]')?.scrollTop || 0;
        panel.innerHTML = freshPanel.innerHTML;
        const body = this.querySelector('[data-kp-cart-body]');
        if (body) body.scrollTop = scroll;
        if (this.classList.contains('is-open')) {
          const [line, label] = (this.focusKey || '').split(':');
          const target = line && this.querySelector(`[data-line="${line}"] [aria-label="${label}"]`);
          (target || this.querySelector('[data-kp-drawer-close].kp-icon-btn'))?.focus();
        }
      }

      async reload() {
        const res = await nativeFetch(`${root}?sections=${DRAWER_SECTION}`);
        const data = await res.json();
        this.update(data[DRAWER_SECTION]);
      }
    }
    KpCartDrawer.portal = false;
    customElements.define('kp-cart-drawer', KpCartDrawer);
  };

  const drawer = () => document.getElementById('kp-cart-drawer');

  document.addEventListener('kp:cart-updated', (event) => {
    const html = event.detail.sections && event.detail.sections[DRAWER_SECTION];
    if (html) drawer()?.update(html);
    else drawer()?.reload();
  });

  document.addEventListener('kp:cart-open', (event) => {
    const d = drawer();
    if (d && d.open && d.hidden) d.open(event.detail && event.detail.trigger);
  });

  // Legacy Electro product page still emits "cart-add"
  document.addEventListener('cart-add', async () => {
    const d = drawer();
    if (!d) return;
    await d.reload();
    if (d.hidden) d.open();
  });

  // Header cart icons open the drawer instead of navigating
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-kp-cart-open], .js-header-cart-icon--desktop');
    const d = drawer();
    if (!link || !d || !d.open || document.body.classList.contains('template-cart')) return;
    event.preventDefault();
    d.open(link);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', defineDrawer);
  else defineDrawer();
})();
